import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { getImageSrc, useVariantImageUrlMap } from "components/Tableimageshelpers";
import { uploadFilesAndGetIds, hardDeletePresignedFiles } from "components/Imageshelper";
import ConnectVendorModal from "components/ConnectVendorModal";
import VendorDetailsSlider from "components/VendorDetailsSlider";
import "../../assets/css/SignageOrders.css";
import { getAuthCookie } from "../../utils/authCookies";

const API = process.env.REACT_APP_API_URL;

// ---------- Fallback (shown if fetch/token fails) ----------
// -----------------------------------------------------------

export type VendorProfile = {
    _id: string;
    user_id: string;
    vendor_name: string;
    category: string;
    tag_line?: string;
    description?: string;
    about_company?: string;
    logo?: string;
    banner?: string;
    contact_info?: {
        website?: string;
        email?: string;
        phone?: string;
        location?: {
            label?: string;
            addressLine1?: string;
            addressLine2?: string;
            city?: string;
            state?: string;
            country?: string;
            zip?: string;
            cityCode?: string;
            stateCode?: string;
            countryCode?: string;
            latitude?: number;
            longitude?: number;
            phone?: string;
        };
    };
    business_details?: {
        year_of_established?: number;
        number_of_employees?: number;
        response_time?: number;
        completed_projects?: number;
        price_model?: string;
    };
    core_specialties?: string[];
    certifications_compliance?: string[];
    why_choose_us?: string;
    verified?: boolean;
    partner?: boolean;
    status?: string;
    catalog_visibility?: string;
};
type CategoryRow = {
    _id?: string;
    id?: string;
    category_id?: string;
    name?: string;
    label?: string;
    title?: string;
    slug?: string;
    product_count?: number;
    images?: any[];
    image?: any;
    count?: number;
    products?: any[];
};

type Props = {
    token?: string | null;
    onViewCategory?: (cat: CategoryRow) => void;
    supplierId?: string; // 👈 add this
};

type ApiCategoriesResponse = {
    data: Array<{ category: string; count: number, image: any, category_id: string, _id: string, id: string }>;
};
export default function MarketplaceCategories({ onViewCategory }: Props) {
    const { id, venderId } = useParams<{ id: string, venderId: string }>();

    localStorage.setItem("venderId", venderId || "");
    const supplierId = localStorage.getItem("supplierId");
    // const supplierId = localStorage.getItem("supplierId");
    // read/sanitize token once
    const raw = getAuthCookie("sessionToken") || "";
    const marketId = localStorage.getItem("profileId");
    const token = raw.replace(/^"|"$/g, ""); // some apps store with quotes
    const userId = getAuthCookie("userId");
    const [profile, setProfile] = useState<VendorProfile | null>(null);
    const [categories, setCategories] = useState<CategoryRow[]>([]);
    const [activeProfile, setActiveProfile] = useState<VendorProfile | null>(null);
    const location = useLocation();
    const isTeammember = location.pathname.includes('/teammember');
    const isAgent = location.pathname.includes('/agent');
    const baseRoute = isTeammember ? '/teammember' : isAgent ? '/agent' : '/realtor';
    const userType: "agent" | "realtor" = isAgent ? "agent" : "realtor";

    const [loading, setLoading] = useState(true);
    const [showConnectModal, setShowConnectModal] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    // Draft typing value; applied value is only updated on Enter
    const [categorySearchInput, setCategorySearchInput] = useState("");
    const [categorySearch, setCategorySearch] = useState("");
    const [categorySort, setCategorySort] = useState<string>("default");

    // Brands -> Categories -> Products: Brand name comes from Brands page via `location.state`.
    const locationState: any = location.state;
    const brandNameFromState: string =
        locationState?.brand?.brand_name ||
        locationState?.brand?.name ||
        locationState?.brandName ||
        "";

    type UploadedImage = { id: string; name: string; size: number };
    const [uploaded, setUploaded] = useState<UploadedImage[]>([]);
    const [tempUploadIds, setTempUploadIds] = useState<Record<string, string[]>>({});
    const navigate = useNavigate();
    // Collect ids we might display
    const allImageIds: string[] = useMemo(() => {
        const ids: string[] = [];

        // category images
        (categories || []).forEach((r) => {
            const id = (r?.images || r?.image) as any;
            if (id) ids.push(String(id));
        });

        // ✅ include the vendor logo id
        if (profile?.logo) ids.push(String(profile.logo));
        // newly uploaded in this session
        uploaded.forEach((u) => ids.push(u.id));

        return Array.from(new Set(ids));
    }, [categories, uploaded, profile?.logo]);



    // Payload expected by useVariantImageUrlMap
    const dataForMap = useMemo(() => {
        if (allImageIds.length === 0) return [];
        return [{
            product: { variants: [{ images: allImageIds.map((url) => ({ url })) }] },
        }];
    }, [allImageIds]);

    const imageUrlMap = useVariantImageUrlMap(dataForMap, token);

    const logoUrl = useMemo(() => {
        const id = profile?.logo;
        if (!id) return "";

        // via map first (handles ids presigned by the helper)
        const viaMap = getImageSrc(id, imageUrlMap);
        if (viaMap) return viaMap;

        // if already an http(s) url, use as-is
        if (/^https?:\/\//i.test(String(id))) return String(id);

        // last fallback if backend serves “/files/:id”
        return `${process.env.REACT_APP_BASE_URL}${id}`;
    }, [profile?.logo, imageUrlMap]);


    useEffect(() => {
        let abort = new AbortController();

        async function load() {
            setLoading(true);
            const commonInit: RequestInit = {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                signal: abort.signal,
            };

            try {
                // 1) Profiles
                let pOk = false;
                try {
                    const resp = await fetch(`${API}/marketplace-profiles/${marketId}`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    });
                    const json = (await resp.json()) as VendorProfile | { error?: string };
                    if (!resp.ok) throw new Error((json as any)?.error || "Failed to load vendor details");
                    setProfile(json as VendorProfile);
                } catch { }
                // if (!pOk) setProfile(DEFAULT_PROFILE);

                // 2) Categories
                let cOk = false;
                try {
                    const cRes = await fetch(`${API}/signage/${supplierId}/marketplace/brands/${id}/categories`, commonInit);
                    if (cRes.ok) {
                        const cJson: ApiCategoriesResponse = await cRes.json();
                        const parsed: CategoryRow[] = Array.isArray(cJson?.data)
                            ? cJson.data.map((d, i) => ({
                                id: `${d.category}-${i}`,
                                category_id: d.category_id || d._id || d.id,
                                name: d.category,
                                count: d.count,
                                images: d.image,
                            }))
                            : [];
                        setCategories(parsed);
                        cOk = true;
                    }
                } catch { }
            } finally {
                setLoading(false);
            }
        }

        load();
        return () => abort.abort();
    }, [token]);

    const title = useMemo(() => {
        const vendor = profile?.vendor_name?.trim() || "Vendor";
        return `${vendor} Product Categories`;
    }, [profile]);

    function categoryName(c: CategoryRow) {
        // also handle raw {category}
        return (c as any).category || c.name || c.label || c.title || "Category";
    }
    function categoryCount(c: CategoryRow) {
        return typeof c.product_count === "number"
            ? c.product_count
            : typeof c.count === "number"
                ? c.count
                : Array.isArray(c.products)
                    ? c.products.length
                    : 0;
    }

    const vendorName = profile?.vendor_name?.trim() || "Vendor";
    const displayCategories = useMemo(() => {
        let list = [...categories];
        const term = categorySearch.trim().toLowerCase();
        if (term) {
            list = list.filter((c) => categoryName(c).toLowerCase().includes(term));
        }
        if (categorySort === "name-asc") list.sort((a, b) => categoryName(a).localeCompare(categoryName(b)));
        if (categorySort === "name-desc") list.sort((a, b) => categoryName(b).localeCompare(categoryName(a)));
        if (categorySort === "products-high") list.sort((a, b) => categoryCount(b) - categoryCount(a));
        if (categorySort === "products-low") list.sort((a, b) => categoryCount(a) - categoryCount(b));
        return list;
    }, [categories, categorySearch, categorySort]);

    const logoColors = ["#FF6B6B", "#4ECDC4", "#FFE66D", "#95E1D3", "#A8EDEA", "#FFB6B9", "#667BC6", "#FF9671", "#A0C4FF"];

    return (
        <div className="overlay-page active" style={{ display: "block" }}>
            {/* <div className="overlay-page-header">
                <div className="overlay-page-header-content">
                    <div className="header-left">
                        <button type="button" onClick={() => navigate(-1)} className="back-btn" style={{ background: "none", border: "none", cursor: "pointer" }}>
                            ←
                        </button>
                        <span className="page-title-text">Product Categories</span>
                    </div>
                </div>
            </div> */}

            <div className="overlay-page-content">
                <div className="vendor-info-card" id="catalogVendorCard">
                    <div className="vendor-info-card-left">
                        <Link to={`${baseRoute}/suppliers/${supplierId || ""}/brand/${id}`} className="vendor-info-card-back" style={{ textDecoration: "none" }}>
                            ←
                        </Link>
                        {/* <div
                            className="vendor-info-card-logo"
                            style={{
                                background: logoUrl ? "transparent" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                color: logoUrl ? "inherit" : "white",
                            }}
                        >
                            {logoUrl ? (
                                <img src={logoUrl} alt={vendorName} style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 10 }} />
                            ) : (
                                vendorName.charAt(0).toUpperCase()
                            )}
                        </div> */}
                        <span className="vendor-info-card-name">{vendorName}</span>
                    </div>
                    <div className="vendor-info-card-right">
                        <button type="button" className="btn-header-details" onClick={() => setShowDetails(true)}>
                            Details
                        </button>
                        <button
                            type="button"
                            className="btn-connect-header"
                            onClick={() => {
                                const sid = venderId || profile?.user_id || supplierId || "";
                                if (sid) localStorage.setItem("supplierId", sid);
                                setShowConnectModal(true);
                            }}
                        >
                            ✓ Connect
                        </button>
                    </div>
                </div>

                <div className="breadcrumb" id="catalogBreadcrumb">
                    <span className="breadcrumb-item" onClick={() => navigate(`${baseRoute}/suppliers`)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate(`${baseRoute}/suppliers`)}>
                        {vendorName}
                    </span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="breadcrumb-item" onClick={() => navigate(`${baseRoute}/suppliers/${supplierId || ""}/brand/${id}`)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate(`${baseRoute}/suppliers/${supplierId || ""}/brand/${id}`)}>
                        Brands
                    </span>
                    <span className="breadcrumb-separator">›</span>
                    <span className="breadcrumb-item active">Categories</span>
                </div>

                <div className="page-header-centered">
                    <h1 className="page-title" id="catalogPageTitle">{title}</h1>
                    <p className="page-subtitle" id="catalogPageSubtitle">Browse categories and explore products</p>
                </div>

                <div className="search-filter-card">
                    <div className="search-filter-left">
                        <span className="results-count" id="categoriesResultsCount">
                            <strong>{displayCategories.length}</strong> categories
                        </span>
                    </div>
                    <div className="search-filter-right">
                        <div className="filter-search-input">
                            <span className="search-icon">🔍</span>
                            <input
                                type="text"
                                id="catalogSearchInput"
                                placeholder="Search categories..."
                                value={categorySearchInput}
                                onChange={(e) => setCategorySearchInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        setCategorySearch(categorySearchInput);
                                    }
                                }}
                            />
                        </div>
                        <div className="filter-divider" />
                        <span className="filter-label">Sort by:</span>
                        <select
                            className="filter-select"
                            id="categoriesSortSelect"
                            value={categorySort}
                            onChange={(e) => setCategorySort(e.target.value)}
                        >
                            <option value="default">Default</option>
                            <option value="name-asc">Name (A-Z)</option>
                            <option value="name-desc">Name (Z-A)</option>
                            <option value="products-high">Most Products</option>
                            <option value="products-low">Fewest Products</option>
                        </select>
                    </div>
                </div>

                {loading ? (
                    <div className="card1-grid">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="card1">
                                <div className="card1-header">
                                    <div className="card1-logo" style={{ background: "#e0e0e0", color: "#999" }}>—</div>
                                </div>
                                <div className="card1-body">
                                    <div className="card11-name">Loading...</div>
                                    <div className="card1-description">—</div>
                                    <div className="card1-stats">
                                        <div className="card1-stat"><span className="card1-stat-label">Products</span><span className="card1-stat-value">—</span></div>
                                    </div>
                                    <div className="card1-footer">
                                        <span className="btn-card-action" style={{ cursor: "default", opacity: 0.7 }}>View</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="card1-grid" id="catalogCategoriesGrid">
                        {displayCategories.map((c, idx) => {
                            const count = categoryCount(c);
                            const name = categoryName(c);
                            const imageId = c?.images || (c?.images?.length ? (c.images as any)[0]?.url : null) || c?.image;
                            const url =
                                getImageSrc(imageId, imageUrlMap) ||
                                (imageId && typeof imageId === "string" && imageId.startsWith("http") ? imageId : (imageId ? `${process.env.REACT_APP_BASE_URL}${imageId}` : ""));

                            return (
                                <div key={c._id || c.id || name} className="card1">
                                    <div className="card1-header">
                                        <div
                                            className="card1-logo"
                                            style={{
                                                background: url ? "transparent" : `linear-gradient(135deg, ${logoColors[idx % logoColors.length]}, ${logoColors[(idx + 1) % logoColors.length]})`,
                                                color: "white",
                                            }}
                                        >
                                            {url ? (
                                                <img src={url} alt={name} style={{ width: 270, height: 180 }} />
                                            ) : (
                                                name.substring(0, 2).toUpperCase()
                                            )}
                                        </div>
                                    </div>
                                    <div className="card1-body" style={{ textAlign: "center" }}>
                                        <div className="card1-name">{name}</div>
                                        <div className="card1-description">
                                            {count} product{count !== 1 ? "s" : ""} available
                                        </div>
                                        <div className="card1-stats">
                                            <div className="card1-stat">
                                                <span className="card1-stat-label">Products</span>
                                                <span className="card1-stat-value">{count}</span>
                                            </div>
                                        </div>
                                        <div className="card1-footer">
                                            <Link
                                                to={`${baseRoute}/signage/${supplierId}/brand/${id}/categoryid/${c.category_id || c.id}`}
                                                state={{ categoryName: name, count, category: c, brandName: brandNameFromState }}
                                                className="btn-card-action"
                                                style={{ display: "block", textAlign: "center", textDecoration: "none", color: "white" }}
                                            >
                                                View {name} →
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {!loading && displayCategories.length === 0 && (
                    <div className="card1-grid">
                        <div className="card1" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "2rem" }}>
                            No categories available.
                        </div>
                    </div>
                )}
            </div>

            <VendorDetailsSlider
                show={showDetails}
                onClose={() => setShowDetails(false)}
                profile={profile}
                logoUrlMap={imageUrlMap}
                onConnect={() => {
                    const sid = venderId || profile?.user_id || supplierId || "";
                    if (sid) localStorage.setItem("supplierId", sid);
                    setShowConnectModal(true);
                }}
            />
            <ConnectVendorModal
                show={showConnectModal}
                onClose={() => setShowConnectModal(false)}
                vendor={profile ? {
                    id: venderId || profile.user_id || "",
                    name: profile.vendor_name || "Vendor",
                    logo: logoUrl || undefined,
                } : null}
                userType={userType}
            />
        </div>
    );
}
